import { ReactElement, useEffect } from 'react';
import { connect } from 'react-redux';
import { usePageTracking } from '../../../../../hooks';
import {
	AnalyticsPageCategory,
	AnalyticsPageType
} from '../../../../../models';

import {
	Asset,
	AppState,
	AppThunkDispatch,
	AssetActions,
	fetchAsset,
	loadDefaultAsset,
	createAsset,
	updateAsset,
	deleteAssetFromCache,
	deleteAllAssetsFromCache
} from '../../../../../redux';
import { BackButton, PageContainer } from '../../../../molecules';
import { AssetManagerForm } from '../../../../organisms';

import './asset-manager-page.styles.scss';

export interface IAssetManagerPageProps {
	asset: Asset;
	fetchAsset: (id: string) => void;
	loadDefaultAsset: () => void;
	createAsset: (mapAsset: Asset) => void;
	updateAsset: (mapAsset: Asset) => void;
	removeFromCache: (casperId: string) => void;
	removeAllFromCache: () => void;
}

export interface IAssetManagerRouteParams {
	assetId?: string;
}

function AssetManagerPageView(
	props: IAssetManagerPageProps & IAssetManagerRouteParams
): ReactElement {
	const {
		assetId,
		asset,
		fetchAsset,
		loadDefaultAsset,
		createAsset,
		updateAsset,
		removeFromCache,
		removeAllFromCache
	} = props;

	usePageTracking(
		AnalyticsPageCategory.ContentManager,
		AnalyticsPageType.Asset,
		assetId
	);

	useEffect(() => {
		if (assetId && asset.id !== assetId) {
			fetchAsset(assetId);
		} else if (!assetId) {
			loadDefaultAsset();
		}
	}, []);

	function hasLoaded(): boolean {
		if (assetId === asset.id || !assetId) {
			return true;
		}
		return false;
	}

	function handleAssetSave(asset: Asset): void {
		if (asset.id) {
			updateAsset(asset);
		} else {
			createAsset(asset);
		}
	}

	return (
		<PageContainer
			hasLoaded={hasLoaded()}
			id="map-asset-manager-page"
			smallPadding
		>
			<BackButton />
			<AssetManagerForm
				asset={asset}
				saveAsset={handleAssetSave}
				removeFromCache={removeFromCache}
				removeAllFromCache={removeAllFromCache}
			/>
		</PageContainer>
	);
}

function mapStateToProps(state: AppState, ownProps: IAssetManagerRouteParams) {
	return {
		assetId: ownProps.assetId,
		asset: state.assets.asset
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<AssetActions>) {
	return {
		fetchAsset: (id: string) => dispatch(fetchAsset(id)),
		loadDefaultAsset: () => dispatch(loadDefaultAsset()),
		createAsset: (mapAsset: Asset) => dispatch(createAsset(mapAsset)),
		updateAsset: (mapAsset: Asset) => dispatch(updateAsset(mapAsset)),
		removeFromCache: (casperId: string) =>
			dispatch(deleteAssetFromCache(casperId)),
		removeAllFromCache: () => dispatch(deleteAllAssetsFromCache())
	};
}

export const AssetManagerPage = connect(
	mapStateToProps,
	mapDispatchToProps
)(AssetManagerPageView);
